import { useEffect, useState } from "react"
import { adminApi, type Translation } from "@/lib/admin-api"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import {
    Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from "@/components/ui/table"
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription
} from "@/components/ui/dialog"
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from "@/components/ui/select"
import {
    DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger
} from "@/components/ui/dropdown-menu"
import { Plus, Search, MoreVertical, Pencil, Trash2, Languages, Loader2 } from "lucide-react"
import { toast } from "sonner"
import { Badge } from "@/components/ui/badge"

const pageLabels: Record<string, string> = {
    home: "Bosh sahifa",
    header: "Header",
    footer: "Footer",
    products: "Mahsulotlar",
    categories: "Kategoriyalar"
}

export default function AdminTranslations() {
    const [translations, setTranslations] = useState<Translation[]>([])
    const [isLoading, setIsLoading] = useState(true)
    const [searchQuery, setSearchQuery] = useState("")
    const [pageFilter, setPageFilter] = useState<string>("all")

    const [formModal, setFormModal] = useState<{ open: boolean; translation: Translation | null }>({ open: false, translation: null })
    const [deleteModal, setDeleteModal] = useState<{ open: boolean; translation: Translation | null }>({ open: false, translation: null })

    const [formData, setFormData] = useState({
        page: "home",
        key: "",
        text_uz: "",
        text_en: "",
        text_ru: "",
        description: ""
    })
    const [isSaving, setIsSaving] = useState(false)
    const [isDeleting, setIsDeleting] = useState(false)

    const fetchTranslations = async () => {
        try {
            setIsLoading(true)
            const data = await adminApi.getTranslations()
            setTranslations(data)
        } catch (error) {
            toast.error("Tarjimalarni yuklashda xatolik")
        } finally {
            setIsLoading(false)
        }
    }

    useEffect(() => {
        fetchTranslations()
    }, [])

    const openFormModal = (translation: Translation | null = null) => {
        if (translation) {
            setFormData({
                page: translation.page,
                key: translation.key,
                text_uz: translation.text_uz,
                text_en: translation.text_en,
                text_ru: translation.text_ru,
                description: translation.description || ""
            })
        } else {
            setFormData({
                page: "home",
                key: "",
                text_uz: "",
                text_en: "",
                text_ru: "",
                description: ""
            })
        }
        setFormModal({ open: true, translation })
    }

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault()
        if (!formData.key || !formData.text_uz) {
            toast.error("Kalit va O'zbek matni majburiy")
            return
        }

        setIsSaving(true)
        try {
            if (formModal.translation) {
                await adminApi.updateTranslation(formModal.translation.id, formData)
                toast.success("Tarjima yangilandi")
            } else {
                await adminApi.createTranslation(formData)
                toast.success("Tarjima yaratildi")
            }
            setFormModal({ open: false, translation: null })
            fetchTranslations()
        } catch (error: any) {
            toast.error(error.message || "Xatolik yuz berdi")
        } finally {
            setIsSaving(false)
        }
    }

    const handleDelete = async () => {
        if (!deleteModal.translation) return
        setIsDeleting(true)
        try {
            await adminApi.deleteTranslation(deleteModal.translation.id)
            toast.success("Tarjima o'chirildi")
            setDeleteModal({ open: false, translation: null })
            fetchTranslations()
        } catch (error) {
            toast.error("O'chirishda xatolik")
        } finally {
            setIsDeleting(false)
        }
    }

    const filteredTranslations = translations.filter(t => {
        const matchesSearch = t.key.toLowerCase().includes(searchQuery.toLowerCase()) ||
            t.text_uz.toLowerCase().includes(searchQuery.toLowerCase())
        const matchesPage = pageFilter === "all" || t.page === pageFilter
        return matchesSearch && matchesPage
    })

    if (isLoading) {
        return (
            <div className="flex items-center justify-center min-h-[60vh]">
                <div className="w-12 h-12 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin" />
            </div>
        )
    }

    return (
        <div className="space-y-6">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div>
                    <h1 className="text-3xl font-bold">Tarjimalar</h1>
                    <p className="text-muted-foreground">Ko'p tillilik sozlamalari</p>
                </div>
                <Button onClick={() => openFormModal()} className="bg-gradient-to-r from-emerald-500 to-teal-600 gap-2">
                    <Plus className="h-5 w-5" />
                    Yangi tarjima
                </Button>
            </div>

            <Card>
                <CardContent className="p-4">
                    <div className="flex flex-col sm:flex-row gap-4">
                        <div className="relative flex-1">
                            <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
                            <Input
                                placeholder="Kalit yoki matn qidirish..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                className="pl-12 h-12 bg-slate-50 dark:bg-gray-800 border-0"
                            />
                        </div>
                        <Select value={pageFilter} onValueChange={setPageFilter}>
                            <SelectTrigger className="w-full sm:w-[180px] h-12">
                                <SelectValue placeholder="Sahifa" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">Barcha sahifalar</SelectItem>
                                {Object.entries(pageLabels).map(([value, label]) => (
                                    <SelectItem key={value} value={value}>{label}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                </CardContent>
            </Card>

            <Card>
                <CardContent className="p-0">
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead>Sahifa</TableHead>
                                <TableHead>Kalit</TableHead>
                                <TableHead>O'zbek</TableHead>
                                <TableHead>Ingliz</TableHead>
                                <TableHead>Rus</TableHead>
                                <TableHead className="w-[80px]"></TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {filteredTranslations.map((translation) => (
                                <TableRow key={translation.id}>
                                    <TableCell>
                                        <Badge variant="outline">{pageLabels[translation.page] || translation.page}</Badge>
                                    </TableCell>
                                    <TableCell className="font-mono text-sm">{translation.key}</TableCell>
                                    <TableCell className="max-w-[150px] truncate">{translation.text_uz}</TableCell>
                                    <TableCell className="max-w-[150px] truncate text-muted-foreground">{translation.text_en || "-"}</TableCell>
                                    <TableCell className="max-w-[150px] truncate text-muted-foreground">{translation.text_ru || "-"}</TableCell>
                                    <TableCell>
                                        <DropdownMenu>
                                            <DropdownMenuTrigger asChild>
                                                <Button variant="ghost" size="icon"><MoreVertical className="h-4 w-4" /></Button>
                                            </DropdownMenuTrigger>
                                            <DropdownMenuContent align="end">
                                                <DropdownMenuItem onClick={() => openFormModal(translation)}>
                                                    <Pencil className="h-4 w-4 mr-2" />Tahrirlash
                                                </DropdownMenuItem>
                                                <DropdownMenuItem onClick={() => setDeleteModal({ open: true, translation })} className="text-red-600">
                                                    <Trash2 className="h-4 w-4 mr-2" />O'chirish
                                                </DropdownMenuItem>
                                            </DropdownMenuContent>
                                        </DropdownMenu>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                    {filteredTranslations.length === 0 && (
                        <div className="text-center py-12">
                            <Languages className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                            <p className="text-muted-foreground">Tarjimalar topilmadi</p>
                        </div>
                    )}
                </CardContent>
            </Card>

            {/* Form Modal */}
            <Dialog open={formModal.open} onOpenChange={(open) => setFormModal({ open, translation: null })}>
                <DialogContent className="max-w-2xl">
                    <DialogHeader>
                        <DialogTitle>{formModal.translation ? "Tarjimani tahrirlash" : "Yangi tarjima"}</DialogTitle>
                    </DialogHeader>
                    <form onSubmit={handleSubmit} className="space-y-4">
                        <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <Label>Sahifa</Label>
                                <Select value={formData.page} onValueChange={(value) => setFormData({ ...formData, page: value })}>
                                    <SelectTrigger><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        {Object.entries(pageLabels).map(([value, label]) => (
                                            <SelectItem key={value} value={value}>{label}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                            <div className="space-y-2">
                                <Label>Kalit *</Label>
                                <Input value={formData.key} onChange={(e) => setFormData({ ...formData, key: e.target.value })}
                                    placeholder="hero_title" className="font-mono" disabled={!!formModal.translation} />
                            </div>
                        </div>
                        <div className="space-y-2">
                            <Label>O'zbekcha *</Label>
                            <Textarea value={formData.text_uz} onChange={(e) => setFormData({ ...formData, text_uz: e.target.value })}
                                placeholder="O'zbek tilidagi matn" rows={2} />
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <Label>Inglizcha</Label>
                                <Textarea value={formData.text_en} onChange={(e) => setFormData({ ...formData, text_en: e.target.value })}
                                    placeholder="English text" rows={2} />
                            </div>
                            <div className="space-y-2">
                                <Label>Ruscha</Label>
                                <Textarea value={formData.text_ru} onChange={(e) => setFormData({ ...formData, text_ru: e.target.value })}
                                    placeholder="Русский текст" rows={2} />
                            </div>
                        </div>
                        <div className="space-y-2">
                            <Label>Izoh (ixtiyoriy)</Label>
                            <Input value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                                placeholder="Bu tarjima nima uchun ishlatiladi" />
                        </div>
                        <DialogFooter>
                            <Button type="button" variant="outline" onClick={() => setFormModal({ open: false, translation: null })}>Bekor</Button>
                            <Button type="submit" disabled={isSaving} className="bg-emerald-500">
                                {isSaving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}Saqlash
                            </Button>
                        </DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>

            {/* Delete Modal */}
            <Dialog open={deleteModal.open} onOpenChange={(open) => setDeleteModal({ open, translation: null })}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Tarjimani o'chirish</DialogTitle>
                        <DialogDescription>"{deleteModal.translation?.key}" ni o'chirmoqchimisiz?</DialogDescription>
                    </DialogHeader>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setDeleteModal({ open: false, translation: null })}>Bekor</Button>
                        <Button variant="destructive" onClick={handleDelete} disabled={isDeleting}>
                            {isDeleting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}O'chirish
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    )
}
